// Pure customer-display state builder (admin inputs). Ported from
// cartadigital-pos app/domain/customer-display/buildCustomerDisplayState.ts,
// adapted to admin's POS V2 draft shape (IPosDraft cart lines carry
// ISelectedOptionsMap instead of the -pos flattened options[] array).
import type { IItem } from '@/interfaces/IItem';
import type { IRestaurant } from '@/interfaces/IRestaurant';
import type { IPosDraft } from '@/domain/pos/pos-types';
import type { ISelectedOptionsMap } from '@/domain/pos/item-options-validation';
import { adaptItemOptions } from '@/domain/pos/option-adapter';
import { formatCartItemOptions } from '@/domain/pos/order-formatting';
import {
  CUSTOMER_DISPLAY_PROTOCOL_VERSION,
  type CustomerDisplayColorMode,
  type ICustomerDisplayOrderLine,
  type ICustomerDisplayPayload,
} from './customer-display.types';

export interface IBuildCustomerDisplayStateInput {
  routeName: string | null;
  activeDraft: IPosDraft | null;
  /** Only branding fields are read — keeps fixtures small and the fn pure. */
  restaurant: Pick<IRestaurant, 'name' | 'logo'> | null;
  /** Menu item map (id → item); null while the catalog is still loading. */
  itemMap: Record<number, IItem> | null;
  /** Resolved admin color mode so the customer display can match it. */
  colorMode?: CustomerDisplayColorMode;
  /**
   * Public CDN base URL used to turn logo paths into absolute URLs
   * (Globals.uploadsPath). Example: "https://cdn.queresto.com".
   */
  uploadsPath: string | null | undefined;
  /** Small platform attribution derived from the Globals domain. */
  platformLabel: string;
  formatComputedMoney: (amount: number) => string;
}

const DEFAULT_STORE_NAME = 'QueResto';
const MAX_LINE_SUBTITLE_CHARS = 140;

/** Route name of the POS V2 order workspace (the only order-mode route). */
const POS_ORDER_ROUTE_NAME = 'restaurantPosOrder';

function normalizeInlineText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function truncateWithEllipsis(value: string, maxChars: number): string {
  if (value.length <= maxChars) return value;
  if (maxChars <= 1) return '…';
  return `${value.slice(0, maxChars - 1).trimEnd()}…`;
}

function isAbsoluteUrl(value: string): boolean {
  // RFC 3986 scheme: ALPHA *( ALPHA / DIGIT / "+" / "-" / "." ) ":"
  return /^[A-Za-z][A-Za-z0-9+.-]*:/.test(value);
}

function buildCdnUrl(uploadsPath: string, logoPath: string): string {
  const base = uploadsPath.endsWith('/') ? uploadsPath : `${uploadsPath}/`;
  const path = logoPath.startsWith('/') ? logoPath.slice(1) : logoPath;
  return new URL(path, base).toString();
}

/** Sum of positive selected-option quantities across all option groups. */
function countSelectedOptions(optionValues: ISelectedOptionsMap): number {
  let count = 0;
  for (const inner of Object.values(optionValues ?? {})) {
    for (const val of Object.values(inner ?? {})) {
      if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
        count += Math.trunc(val);
      }
    }
  }
  return count;
}

export function buildCustomerDisplayState(
  input: IBuildCustomerDisplayStateInput
): ICustomerDisplayPayload {
  const colorMode: CustomerDisplayColorMode = input.colorMode ?? 'dark';

  const restaurantName =
    typeof input.restaurant?.name === 'string' && input.restaurant.name.trim()
      ? input.restaurant.name.trim()
      : DEFAULT_STORE_NAME;

  const logoRaw =
    typeof input.restaurant?.logo === 'string' && input.restaurant.logo.trim()
      ? input.restaurant.logo.trim()
      : null;

  const logoUrl = (() => {
    if (!logoRaw) return null;
    if (isAbsoluteUrl(logoRaw)) return logoRaw;

    const uploadsPath =
      typeof input.uploadsPath === 'string' && input.uploadsPath.trim()
        ? input.uploadsPath.trim()
        : null;
    if (!uploadsPath) return null;

    try {
      return buildCdnUrl(uploadsPath, logoRaw);
    } catch {
      return null;
    }
  })();

  const branding = {
    storeName: restaurantName,
    logoUrl,
    platformLabel: input.platformLabel,
  } as const;

  const activeCartLines =
    input.activeDraft?.cartLines.filter((line) => line.canceledAt == null) ??
    [];

  const shouldShowOrder =
    input.routeName === POS_ORDER_ROUTE_NAME &&
    input.activeDraft != null &&
    activeCartLines.length > 0;

  if (!shouldShowOrder) {
    return {
      protocolVersion: CUSTOMER_DISPLAY_PROTOCOL_VERSION,
      branding,
      colorMode,
      mode: 'idle',
    };
  }

  const lines: ICustomerDisplayOrderLine[] = activeCartLines.map((line) => {
    const item = input.itemMap?.[line.itemId] ?? null;

    const name =
      typeof item?.name === 'string' && item.name.trim()
        ? item.name.trim()
        : `Producto #${line.itemId}`;

    const qty =
      typeof line.quantity === 'number' && Number.isFinite(line.quantity)
        ? Math.max(1, Math.trunc(line.quantity))
        : 1;

    const subtitle = (() => {
      const selectedCount = countSelectedOptions(line.optionValues);
      if (selectedCount <= 0) return null;

      const formatted = item
        ? formatCartItemOptions(line.optionValues, adaptItemOptions(item))
        : '';

      if (formatted) {
        return truncateWithEllipsis(
          normalizeInlineText(formatted),
          MAX_LINE_SUBTITLE_CHARS
        );
      }

      // Option titles unresolvable (item/options gone from the catalog):
      // still tell the customer that selections exist.
      return selectedCount === 1 ? '1 opción' : `${selectedCount} opciones`;
    })();

    return {
      qty,
      name,
      ...(subtitle ? { subtitle } : {}),
      lineTotalLabel: input.formatComputedMoney(line.totalPrice),
    };
  });

  const totalLabel = input.formatComputedMoney(
    input.activeDraft?.totals.total ?? 0
  );

  return {
    protocolVersion: CUSTOMER_DISPLAY_PROTOCOL_VERSION,
    branding,
    colorMode,
    mode: 'order',
    order: { lines, totalLabel },
  };
}
